import React from 'react'
import { string, number, bool, oneOf, oneOfType, arrayOf, shape } from 'prop-types'

import SectionContainer from 'components/atoms/SectionContainer'
import Container from 'components/atoms/Container'
import Row from 'components/atoms/Row'
import Col from 'components/atoms/Col'
import SectionTitle from 'components/atoms/SectionTitle'
import Typography from 'components/atoms/Typography'
import Markdown from 'components/atoms/MarkdownContent'
import { SVGIcon } from 'components/qstrap/SVGIcon'
import SimplePieChart from 'components/molecules/SimplePieChart'
import AdmissionDownloadBrochure from 'components/molecules/AdmissionDownloadBrochure'
import ImagesGallery from 'components/organisms/ImagesGallery'

const ProfileDetailItem = ({ label, value, link, href, linkType, iconName }) => {
  const values = link && value.split(',').map(v => v.trim())

  return (
    <Row className="school-profile__detail-item">
      <Col xs="1">
        <SVGIcon name={iconName} size="md" />
      </Col>
      <Col xs="4">
        <Typography variant="body" className="school-profile__detail-key">{label}</Typography>
      </Col>
      <Col xs="7">
        <Typography variant="body" className="school-profile__detail-val" title={value}>
          {
            link ?
            values.map((v, idx) => (
              <>
                <a href={linkType ? `${linkType}:${v}` : href} key={`sch-plink-${idx}-${v}`}>
                  {`${v}`}
                </a>
                {idx < values.length - 1 && ', '}
              </>
            )) :
            value
          }
        </Typography>
      </Col>
      <style jsx>
        {`
          .school-profile {
            :global(&__detail-item) {
              padding: 12px;
              :global(svg.icon) {
                color: #0fc3c4;
                width: 24px;
                height: 24px;
              }
            }
            :global(&__detail-val) {
              font-weight: 700;
              word-break: break-all;
              max-width: 100%;
              display: block;
              a {
                color: inherit;
              }
            }
          }
        `}
      </style>
    </Row>
  )
}

ProfileDetailItem.propTypes = {
  label: string,
  iconName: string,
  value: oneOfType([string, number]),
  link: bool,
  href: string,
  linkType: oneOf(['tel', 'mailto']),
}

const SchoolProfile = ({
  accreditation,
  brochure,
  description,
  email,
  images,
  phoneNumber,
  studentCount,
  teacherCount,
  teacherStudentRatio,
  tuitionFee,
  website,
}) => (
  <div className="school-profile" id="profile">
    <SectionContainer
      paddingTop={80}
      paddingBottom={80}
    >
      <Container>
        <SectionTitle title="Informasi Umum" />
        <Row>
          <Col md={(images.length === 0 && !brochure) ? 12 : 6}>
            <div className="school-profile__detail">
              {
                accreditation &&
                <div className="school-profile__detail-section">
                  <ProfileDetailItem label="Akreditasi" value={accreditation} iconName="certificate"/>
                </div>
              }
              {
                tuitionFee &&
                <div className="school-profile__detail-section">
                  <ProfileDetailItem label="Biaya Kuliah" value={tuitionFee} iconName="tag"/>
                </div>
              }
              {
                !!(studentCount || teacherCount) &&
                <div className="school-profile__detail-section">
                  <div className="school-profile__detail-item">
                    <Row>
                      <Col xs="1">
                        { !!studentCount && <SVGIcon name="student" size="md" style={{ marginBottom: '1rem' }} /> }
                        { !!teacherCount && <SVGIcon name="teacher" size="md" style={{ marginBottom: '1rem' }} /> }
                      </Col>
                      <Col xs="4">
                        {
                          !!studentCount &&
                          <Typography
                            component="p"
                            className="school-profile__detail-key"
                            variant="body"
                            style={{ marginBottom: '.5rem' }}
                          >
                            Siswa
                          </Typography>
                        }
                        {
                          !!teacherCount &&
                          <Typography
                            component="p"
                            className="school-profile__detail-key"
                            variant="body"
                            style={{ marginBottom: '.5rem' }}
                          >
                            Dosen
                          </Typography>
                        }
                      </Col>
                      <Col xs="2" className="school-profile__studteach-num">
                        {
                          !!studentCount &&
                          <Typography
                            component="p"
                            className="school-profile__detail-val"
                            variant="body"
                            style={{ marginBottom: '.5rem' }}
                          >
                            {studentCount}
                          </Typography>
                        }
                        {
                          !!teacherCount &&
                          <Typography
                            component="p"
                            className="school-profile__detail-val"
                            variant="body"
                            style={{ marginBottom: '.5rem' }}
                          >
                            {teacherCount}
                          </Typography>
                        }
                      </Col>
                      <Col sm="5">
                        {
                          !!(studentCount && teacherCount) && (
                            <div className="school-profile__ratio">
                              <SimplePieChart
                                data={[
                                  { name: 'Teacher', value: teacherCount },
                                  { name: 'Student', value: studentCount },
                                ]}
                                colors={['#0fc3c4', '#0056b3']}
                                width={72}
                                height={72}
                              />
                              <Typography component="p" variant="body">
                                Ratio: {teacherStudentRatio}
                              </Typography>
                            </div>
                          )
                        }
                      </Col>
                    </Row>
                  </div>
                </div>
              }
              {
                (email || phoneNumber || website) &&
                <div className="school-profile__detail-section">
                  { website && <ProfileDetailItem label="Website" value={website} iconName="web" />}
                  {
                    email &&
                    <ProfileDetailItem
                      label="Email"
                      value={email}
                      href={`mailto:${email}`}
                      iconName="mail"
                      linkType="mailto"
                      link
                    />
                  }
                  {
                    phoneNumber &&
                    <ProfileDetailItem
                      label="Telepon"
                      value={phoneNumber}
                      href={`tel:${phoneNumber}`}
                      iconName="phone"
                      linkType="tel"
                      link
                    />
                  }
                </div>
              }
            </div>
            <Markdown content={description} className="school-profile__description"/>
          </Col>
          <Col md={{ size: 5, offset: 7 }} className="school-profile__gallery">
            {
              images.length > 0 &&
              <ImagesGallery images={images}/>
            }
            {
              brochure &&
              <AdmissionDownloadBrochure className="school-profile__button" />
            }
          </Col>
        </Row>
      </Container>
    </SectionContainer>
    <style jsx>
      {`
        .school-profile {
          &__detail {
            margin-bottom: 24px;
          }
          :global(&__detail-key) {
            color: #6b8fbc;
          }
          &__detail-section {
            padding: 12px 0;
            border-top: solid 1px #bed2e8;
            &:nth-last-child(1) {
              border-bottom: solid 1px #bed2e8;
            }
          }
          &__ratio {
            display: flex;
            align-items: center;
          }
          :global(&__button) {
            font-size: 14px;
            font-weight: bold;
            padding: 14px 28px;
            border: solid 2px #0fc3c4;
            width: 100%;
            border-radius: 8px;
            margin: 8px 0;
            box-shadow: none;
          }
          :global(&__studteach-num) {
            @media screen and (max-width: 575px) {
              grid-column: span 7 !important;
            }
          }
          :global(&__gallery) {
            width: 100%;
          }
        }
      `}
    </style>
  </div>
)

SchoolProfile.propTypes = {
  accreditation: string,
  description: string,
  email: string,
  phoneNumber: string,
  studentCount: number,
  teacherCount: number,
  teacherStudentRatio: string,
  tuitionFee: string,
  website: string,
  brochure: string,
  images: arrayOf(shape({
    medium_url: string,
    thumb_url: string,
    original_url: string,
  })),
}

export default SchoolProfile
